import type { ComponentPropsWithRef, ReactNode } from "react";
import { Fragment, forwardRef } from "react";
import { BlockSection } from "@/react/components/shared/BuilderUI/BlockSection";
import { useTopBlockStyles } from "@circle-react-shared/BuilderUI/Hooks/useTopBlockStyles";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { secondaryBlockDefaultStyles } from "../../constants";

export interface HomepageBlock6Props
  extends Omit<ComponentPropsWithRef<"div">, "content"> {
  cards?: ReactNode;
  content?: ReactNode;
  footer?: ReactNode;
  header?: ReactNode;
  styles?: Record<string, StyleValue>;
}

export const HomepageBlock6 = forwardRef<HTMLDivElement, HomepageBlock6Props>(
  ({ header, content, cards, footer, styles, className, ...rest }, ref) => {
    const { backgroundStyles, paddingStyles, marginStyles } = useTopBlockStyles(
      {
        styles,
        defaultStyles: secondaryBlockDefaultStyles,
      },
    );

    return (
      <BlockSection ref={ref} className={className} {...rest}>
        <BlockSection.Background
          className="wb-bg-secondary"
          style={backgroundStyles}
        >
          <BlockSection.Container>
            <BlockSection.Content
              style={{
                ...paddingStyles,
                ...marginStyles,
              }}
            >
              <BlockSection.NestedBlock className="wb-mx-auto wb-flex wb-max-w-3xl wb-flex-col wb-items-center wb-gap-4 wb-text-center md:wb-gap-6">
                {header}
              </BlockSection.NestedBlock>
              <BlockSection.NestedBlock className="wb-flex wb-flex-col wb-gap-10 md:wb-gap-16">
                {content}
              </BlockSection.NestedBlock>
              <BlockSection.NestedBlock className="wb-flex wb-flex-col wb-gap-6 md:wb-gap-8 md:wb-grid md:wb-grid-cols-2">
                {cards}
              </BlockSection.NestedBlock>
              <BlockSection.NestedBlock
                as={Fragment}
                block={footer}
                render={footer => (
                  <div className="wb-mx-auto wb-flex wb-flex-col wb-justify-center wb-gap-10 md:wb-gap-16">
                    <div className="wb-flex wb-flex-col wb-gap-4 wb-text-center md:wb-gap-8 wb-items-center">
                      {footer}
                    </div>
                  </div>
                )}
              />
            </BlockSection.Content>
          </BlockSection.Container>
        </BlockSection.Background>
      </BlockSection>
    );
  },
);

HomepageBlock6.displayName = "HomepageBlock6";
